// The half of money that belongs to the host: what a document may hold, what a
// teacher is told when it may not, and how a number is drawn.
//
// **The arithmetic is `core/src/money.rs`** (#214). `exponentFor`,
// `minorPerUnit`, `fromDecimal`, `toDecimal`, `sum` and `toAmountField` cross
// through `./core.js`.
//
// Why integers, never floats — a balance is an unbounded fold and two offline
// devices must land on the same cent — is the header of `core/src/money.rs`.
//
// ── What stays here ────────────────────────────────────────────────────────
//
//   * `AMOUNT_TOO_LARGE` is **copy**, a sentence a teacher reads, held here for
//     the module-scope readers and pinned to the core's table.
//   * `format` is an **environment reader**, the `clock.js` kind: it needs ICU,
//     and its output differs by platform and ICU version, so
//     `core/src/money.rs` omits it by name.
//   * `MAX_MONEY_MINOR` and `isStorableMinor` are a **refusal** duplicated in
//     `firestore.rules` and pinned there by `expenseBounds.test.js`; moving it
//     into the core would change which two places hold it, not how many.
//     #225 is the same question about `NOTE_BODY_MAX`.

import { coreSync } from './core.js';

// The currency table that says how many decimal places a minor unit has is
// `core/src/money.rs`'s `EXPONENTS`, reached below through `exponentFor`. It
// is the only one: the report PDF service vendors the core (#463), and
// `./locale.js`'s region table is checked against it by
// test/domain/reportPdfMoney.test.js.

// ── The most money one row may hold (#155) ──────────────────────────────────
//
// **The hard boundary is not int64, it is `Number.MAX_SAFE_INTEGER`**, though
// `firestore.rules` says `amountMinor is int`: `toNumber` in @firebase/firestore
// serialises a number as `integerValue` only when it is a safe integer, and as
// `doubleValue` otherwise. So 1e302 is `Number.isInteger` true, a double on the
// wire, and a `permission-denied` at the boundary.
//
// **A bound at the safe maximum would still be wrong**, because money is folded
// rather than read. `financialSummary` and `monthlySeries` sum every entry in a
// window, `balance_owing` in core/src/ledger.rs folds a family's whole ledger,
// and no rule sees a sum, because none is stored. Two entries at the safe
// maximum add up past it. The Rust fold casts with `as i64`, which saturates
// rather than trapping.
//
// So the ceiling leaves the fold room:
//
//   * `MAX_OCCURRENCES` is 1000, so the largest single recurring rule expands
//     to 1e14 minor units — seventy times inside the safe range.
//   * 90,071 separate entries at the absolute ceiling can be summed before the
//     total leaves it.
//
// **In minor units**, because that is what the document holds and what a rule
// can check — firestore.rules does not know a studio's currency. A
// two-decimal studio gets a billion of its own unit and a yen studio a
// hundred billion yen.
//
// MMS's own, lower ceiling is at `AMOUNT_TOO_LARGE` below.
//
// **`validLedgerEntry` and `optionalMinor` carry this too (#164)**, on the
// collection the fold reads. `useAttendance`'s charge is `priceOccurrence()` of
// a bounded `rateMinor` or `priceOverrideMinor`, times at most 24 (a
// 1,440-minute day over a 60-minute hourly rate), still far inside this
// ceiling. `AddTransactionDrawer`, `useAddStudentForm` and
// `StudentDetailPage`'s package sale refuse client-side with
// `AMOUNT_TOO_LARGE`/`amountError` below, the #115 shape `expenseEntries` uses
// (#155).
//
// **One ceiling for a rate and a transaction** (#164): the ceiling is about
// what a *sum* can safely be, and a rate's sums are no larger than a
// transaction's. A second constant would be #145's drift.
//
// `priceOverrideMinor`'s two writers — the event form (`useEventForm.js`) and
// the attendance drawer's own override field (`useAttendanceForm.js`) — reach
// the same `optionalMinor` clause (#171).
//
// **Written three times in firestore.rules and once here.**
// `test/domain/expenseBounds.test.js` pins the `expenseEntries` clause, and
// `test/domain/money.test.js` pins `validLedgerEntry` and `optionalMinor` —
// which bound `rateMinor`, `priceOverrideMinor` and `priceMinor` — and asserts
// that no other clause bounds a figure of this magnitude at a different number
// (#341, #225).
export const MAX_MONEY_MINOR = 100000000000;

// Money as a document may hold it: an integer, not negative, inside the ceiling
// above. Not a clamp (#93): a rent of 1e302 silently filed as a billion is
// worse than a refusal. The callers refuse.
//
// `isSafeInteger` is redundant while the ceiling stands (the ceiling is four
// orders of magnitude below `Number.MAX_SAFE_INTEGER`, and no test tells it
// from `isInteger`). It states the *boundary's* rule, which keeps holding if
// MAX_MONEY_MINOR is ever raised past 2^53.
export function isStorableMinor(minor) {
  return Number.isSafeInteger(minor) && minor >= 0 && minor <= MAX_MONEY_MINOR;
}

// Ours: MMS's own ceiling (#246) is **$10,000,000.00** — a hundredth of ours —
// refused with a sentence at the field, "Please enter an amount less than or
// equal to $10,000,000.00." See MAX_MONEY_MINOR above. Generic wording: every
// caller is a rate, a package price, or a ledger transaction, and a rate takes
// the same ceiling as a transaction (#164).
export const AMOUNT_TOO_LARGE = 'That amount is too large to store.';

// Whether a typed decimal amount would refuse at the ceiling, and the sentence
// to show if so — #115's shape: a form judges this *before* Save rather than
// let firestore.rules answer with a bare permission-denied.
//
// **Not useExpenseEntryForm.js's check**, which also judges a typed minus
// (#166) and a required field, both specific to an expense's signed Amount.
// This answers only whether the magnitude is storable, and leaves "is it
// required" and "is a minus meaningful" to the form (a student on
// `pricingMode: 'none'` may skip the field entirely).
//
// Returns `null` for an empty or non-finite typed value, as `durationError` in
// domain/eventSeries.js does: the core's `from_decimal` turns a non-finite
// input into 0, so finiteness is asked of the typed string first.
//
// **The check is `money::amount_error` in the core**, so a phone's rate field
// refuses at the same amount: it reads the typed string with JavaScript's
// `Number()` rules (`js_string_to_number`) and converts it with the same
// `from_decimal`, so the amount judged is the amount that would be stored.
// `core/src/people_forms_vectors.json` holds the recorded answers.
export function amountError(text, currency = 'USD') {
  const refusal = coreSync().amountError(String(text ?? ''), currency);
  return refusal ? refusal.message : null;
}

// ── A third decimal in a two-decimal currency (#246) ────────────────────────
//
// In MMS's Add Transaction Amount field, typing `50.005` reads `$ 50.00` and
// `50.999` reads `$ 50.99`: the third decimal never enters the field — the
// keystroke is swallowed, with no message. **Truncation by refusing the
// character**, not rounding.
//
// `amountError` above judges only the ceiling, so without this `50.999` would
// be scaled by `from_decimal` to **5100**, half-up, with nothing on screen
// saying a number had changed. So the typed text is cut to the currency's
// decimal places on the way into form state, and there is nothing left for the
// conversion to round, and no sentence to show.
//
// ── Cut as a string, before any float exists ───────────────────────────────
//
// `core-ffi`'s `fromDecimal` takes an `f64`, so the digits a teacher typed are
// gone by the time it runs, and truncating a binary float is not the same
// operation:
//
//   * `8.87 * 100` is `886.99999999999988` — a plain `trunc` files $8.86.
//   * `from_decimal_scaled`'s `1e-6` nudge covers that only while the scaled
//     value is small. At the ceiling one ULP is ~3e-5, thirty times the nudge,
//     and a nudge big enough for the large end swallows a genuine 0.1 at the
//     small end.
//
// The core's `money::clamp_amount_decimals` takes the typed string for that
// reason.
//
// **Every amount field in the app calls this** (#246, #308). MMS's behaviour
// is known for the Add Transaction drawer; the other sites assume MMS uses the
// same editor on every amount box:
//
//   * the student rate, add and edit — `src/hooks/useAddStudentForm.js`,
//     `src/hooks/useStudentEdit.js`
//   * the lesson price override — `src/hooks/useEventForm.js`
//   * the attendance price and Amount Paid —
//     `src/components/calendar/attendance/AttendanceTab.jsx`
//   * the expense Amount — `src/hooks/useExpenseEntryForm.js`
//   * the package price — `src/pages/StudentDetailPage.jsx`
//   * the Add Transaction amount — `src/components/families/AddTransactionDrawer.jsx`
//   * and the expense list's By Amount *search* box —
//     `src/hooks/useExpenseList.js` — which writes nothing, but is parsed
//     by the same `from_decimal` and so would search for a figure the
//     teacher had not typed.
//
// The clamp goes on the way into form state, never on the way out: the field
// has to show what it holds, or the next keystroke is appended to a value the
// teacher cannot see.
//
// **The clamp is `money::clamp_amount_decimals` in the core**, so a phone's
// amount box swallows the same keystroke. Its three cases: an
// `<input type="number">` will hand over `1e5` or `1.5e3`, which has no decimal
// places to count, and is left alone (cutting it would turn 1500 into
// nothing); a zero-exponent currency loses the separator too — ¥1200, never
// ¥1200.00; and a three-decimal currency keeps three — `50.005` is a whole
// five fils in KWD.
export function clampAmountDecimals(text, currency = 'USD') {
  return coreSync().clampAmountDecimals(String(text ?? ''), currency);
}

// The step an amount field should offer, in the currency it is typed in.
//
// `step="0.01"` hard-coded is wrong outside the dollar: it makes a yen figure
// look as though it has cents, and in KWD it marks a legitimate third decimal
// `:invalid` (#246).
export function amountStep(currency = 'USD') {
  const exp = coreSync().exponentFor(currency);
  return exp === 0 ? '1' : `0.${'0'.repeat(exp - 1)}1`;
}

// MMS's own sentence on its Add Payment drawer (#180) when Amount is cleared
// and Save clicked: the field outlines red, this appears beneath it, and Save
// stays enabled and refuses at submit.
//
// The same string as `REQUIRED` in `src/hooks/useExpenseEntryForm.js`, not
// imported from there: that is a form hook for a different screen.
export const AMOUNT_REQUIRED = 'This field is required';

// Integer minor units -> an exact decimal string. No float, ever.
//
// Mirrored by formatMinor() in services/report-pdf/src/format.js, which
// test/domain/reportPdfMoney.test.js holds to this file's `format` output for
// every region's currency. `minor / 100` makes a Number, and a Number cannot
// hold every integer the ledger can fold. 12345678901234567 cents is a real BigInt and a
// wrong Double. The digits are shifted by string surgery instead, so the value
// handed to Intl is the value the ledger holds.
//
// Not exported: it is an implementation detail of format(), and nothing outside
// display should be turning money into text at all.
function toMinorBigInt(minor) {
  if (typeof minor === 'bigint') return minor;
  // A digit string stays exact. Number('12345678901234567') is already the
  // wrong integer before anything here gets a look at it, so a caller holding
  // a figure past 2^53 has to hand it over as a BigInt or as digits — there is
  // no repair to do once it is a Double.
  if (typeof minor === 'string' && /^[+-]?\d+$/.test(minor.trim())) return BigInt(minor.trim());
  return BigInt(Math.trunc(Number(minor) || 0));
}

function toDecimalString(minor, currency) {
  const exp = coreSync().exponentFor(currency);
  const n = toMinorBigInt(minor);
  const negative = n < 0n;
  const digits = (negative ? -n : n).toString().padStart(exp + 1, '0');
  const whole = digits.slice(0, digits.length - exp) || '0';
  const frac = exp ? `.${digits.slice(digits.length - exp)}` : '';
  return `${negative ? '-' : ''}${whole}${frac}`;
}

// Money -> the string a person reads. Currency and locale both matter, and
// neither has a safe default, so both are threaded from the studio's setting —
// see ./locale.js and src/context/LocaleContext.jsx. The USD/undefined defaults
// below are for the tests and for server-side callers that pass a currency
// explicitly; a component should never be relying on them.
//
// In JavaScript because it needs ICU (see the header). The decimal places come
// from the core's `exponentFor`, the same table the arithmetic uses, so a KWD
// figure is never rounded to two.
//
// Locale *derivation* is a different question and lives in ./locale.js; this is
// only the rendering of a number the domain already decided.
export function format(minor, currency = 'USD', locale = undefined) {
  const exp = coreSync().exponentFor(currency);
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: exp,
    maximumFractionDigits: exp,
    // A *string* argument, not a Number. Intl.NumberFormat formats a decimal
    // string exactly, with no intermediate Double. Passing the result of
    // toDecimal() here would be wrong only once the figure is large.
  }).format(toDecimalString(minor, currency));
}

// An amount as MyMusicStaff writes a negative one on a family's ledger and an
// invoice (#649): `($10.00)`, not `-$10.00`. The magnitude is
// written by `format` above, in the same currency and Region; the
// parentheses, and why every Region gets them, are the core's
// (`money::in_parentheses`), so the PDF and both phones write the same
// characters. Which figures are written this way is decided where they are:
// a ledger pill's `sign` (`ledgerRowView`), an invoice body's `negatives`.
export function formatInParentheses(minor, currency = 'USD', locale = undefined) {
  const n = toMinorBigInt(minor);
  const sign = n < 0n ? -1 : (n > 0n ? 1 : 0);
  return coreSync().moneyInParentheses(sign, format(n < 0n ? -n : n, currency, locale));
}

// The sign `format` would put on an amount — `$` in en-US, `£` in en-GB, `€`
// in de-DE, `CA$` for Canadian dollars read in en-US — for a box that shows
// it beside what is typed. Asked of the same `Intl.NumberFormat` as `format`,
// so the two cannot disagree; the currency's code if the runtime has no sign.
export function currencySymbol(currency = 'USD', locale = undefined) {
  try {
    const part = new Intl.NumberFormat(locale, { style: 'currency', currency })
      .formatToParts(0)
      .find((p) => p.type === 'currency');
    return part ? part.value : currency;
  } catch {
    return currency;
  }
}
